/** "?" help for the order panel and mobile ticket, filled in with the order's own numbers. */
import { D, fmt, fmtPrice, fmtUsd } from '@platform/shared';
import type { HelpText } from './HelpTip';
import type { useOrderForm } from './useOrderForm';

type Form = ReturnType<typeof useOrderForm>;
/** Academy lesson behind each "?" (paths; see packages/shared/src/academy). */
const LEARN: Partial<Record<OrderHelpKey, string>> = {
  type: '/academy/first-trade/order-types',
  price: '/academy/first-trade/order-types',
  size: '/academy/how-markets-work/lots-units-usd',
  notional: '/academy/leverage-margin/what-leverage-does',
  margin: '/academy/leverage-margin/margin-used',
  fee: '/academy/first-trade/trading-fees',
  free: '/academy/leverage-margin/cross-margin',
  tpsl: '/academy/first-trade/take-profit-stop-loss',
  leverage: '/academy/leverage-margin/what-leverage-does',
};

export type OrderHelpKey = 'type' | 'price' | 'size' | 'notional' | 'margin' | 'fee' | 'free' | 'pointValue' | 'tpsl' | 'leverage';

/** The fee row's help: the rate on the notional, the flat fee per trade, both, or neither. */
function feeHelp(rate: string, flat: string, feePct: string, rateCalc: string | null, total: string | null): HelpText {
  const hasRate = D.from(rate).isPos();
  const flatUsd = D.from(flat).isPos() ? fmtUsd(flat) : null;
  if (hasRate && flatUsd) {
    return {
      title: 'Fee',
      text: `${feePct}% of the notional plus a flat ${flatUsd}, taken when the order fills. Closing charges the ${feePct}% again, not the ${flatUsd}.`,
      example: rateCalc && total ? `${rateCalc} + ${flatUsd} = ${total}` : undefined,
    };
  }
  if (flatUsd) return { title: 'Fee', text: `A flat ${flatUsd} per trade, taken once when the order fills. Closing the trade costs no fee.` };
  if (hasRate) return { title: 'Fee', text: `A charge of ${feePct}% of the notional, taken when the order fills and again when the trade closes.`, example: rateCalc && total ? `${rateCalc} = ${total}` : undefined };
  return { title: 'Fee', text: 'No fee on this market: the cost of the trade is the spread.' };
}

export function orderHelp(f: Form): Record<OrderHelpKey, HelpText> {
  const px = (x: D | string) => fmtPrice(x, f.dp, f.ccy);
  const long = f.side === 'long';
  const at = f.openRef ? ` (${px(f.openRef)})` : '';
  const c = f.calc;
  const unit = f.inst?.unit ?? '';
  const feePct = D.from(f.feeRate).mul(100).toString();
  const total = c ? c.margin.add(c.fee) : null;

  const typeText: Record<string, HelpText> = {
    Buy: { title: 'Buy', text: `Opens a buy now at the current Ask${at}. You profit if the price goes up.`, example: 'Buy = going long: you open a position that gains when the price rises.' },
    Sell: { title: 'Sell', text: `Opens a sell now at the current Bid${at}. You profit if the price goes down.`, example: 'Sell = going short: you open a position that gains when the price falls.' },
    'Buy Limit': { title: 'Buy Limit', text: `Waits until the price falls to your price, then buys. Use it to buy cheaper than now. The price must be below the Ask${at}.` },
    'Buy Stop': {
      title: 'Buy Stop',
      text: `Waits until the price rises to your price, then buys at market. Use it to catch a breakout. The price must be above the Ask${at}. In a fast market the fill can be a little worse than your price.`,
    },
    'Sell Limit': { title: 'Sell Limit', text: `Waits until the price rises to your price, then sells. Use it to sell higher than now. The price must be above the Bid${at}.` },
    'Sell Stop': {
      title: 'Sell Stop',
      text: `Waits until the price falls to your price, then sells at market. Use it to catch a breakdown. The price must be below the Bid${at}. In a fast market the fill can be a little worse than your price.`,
    },
    'Buy Limit / Stop': { title: 'Limit or Stop', text: `Enter the price you want to buy at. Below the Ask${at} it becomes a Buy Limit (buy cheaper). Above the Ask it becomes a Buy Stop (buy a breakout).` },
    'Sell Limit / Stop': { title: 'Limit or Stop', text: `Enter the price you want to sell at. Above the Bid${at} it becomes a Sell Limit (sell higher). Below the Bid it becomes a Sell Stop (sell a breakdown).` },
  };

  const help: Record<OrderHelpKey, HelpText> = {
    type: typeText[f.orderLabel] ?? { title: 'Order type', text: 'Market opens now; Limit / Stop waits for your price.' },
    price: {
      title: 'Order price',
      text: `The price your order waits for. ${long ? 'Below the Ask it is a Buy Limit, above it a Buy Stop.' : 'Above the Bid it is a Sell Limit, below it a Sell Stop.'} Until it fills it is listed under Open Orders and you can cancel it.`,
      example: f.pendType ? typeText[f.orderLabel]?.text : undefined,
    },
    size: f.byLots
      ? {
          title: 'Volume',
          text: `How big the trade is, in lots. 1 lot = ${fmt(f.lotSize, 0)} ${unit}. Minimum ${f.minLots}, in steps of ${f.lotStep}. Max is the largest size your available margin allows at this leverage.`,
        }
      : { title: 'Amount', text: 'The value of the position in US dollars. Max is the largest value your available margin allows at this leverage.' },
    notional: {
      title: 'Notional',
      text: 'The full value of the position. Profit and loss are calculated on this amount, not on your margin.',
      example: c && f.ref ? `${fmt(c.qty, f.qdp, 'down')} ${unit} × ${px(f.ref)} = ${fmtUsd(f.amt)}` : undefined,
    },
    margin: {
      title: 'Margin required',
      text: 'The part of your money locked to hold this trade: notional ÷ leverage. You get it back when the trade closes. A Limit or Stop order reserves it when you place it.',
      example: c ? (c.hedges ? `Offsets your opposite position, so only ${fmtUsd(c.margin)} more is needed.` : `${fmtUsd(f.amt)} ÷ ${f.lev}x = ${fmtUsd(c.margin)}`) : undefined,
    },
    fee: feeHelp(f.feeRate, f.feeFlat, feePct, c ? `${fmtUsd(f.amt)} × ${feePct}%` : null, c ? fmtUsd(c.fee) : null),
    free: {
      title: 'Free margin after',
      text: 'What stays available for new orders and to absorb losses after this order. Losses on open trades come out of it first.',
      example: total ? `${fmtUsd(f.available)} − ${fmtUsd(total)} margin and fee = ${fmtUsd(D.from(f.available).sub(total))}` : undefined,
    },
    pointValue: {
      title: 'Value of $1 move',
      text: 'How much your profit or loss changes for every $1 the price moves.',
      example: c ? `A 100-point move = ${fmtUsd(c.pointValue.mul(100))}` : undefined,
    },
    tpsl: {
      title: 'Take profit / Stop loss',
      text: `Take profit closes the trade automatically at your profit target. Stop loss closes it automatically to limit a loss. For a buy, TP goes above the entry and SL below; for a sell it is the other way round.`,
    },
    leverage: {
      title: 'Leverage',
      text: `How many times your margin the position is worth. At ${f.lev}x, ${fmtUsd(100)} of margin holds a ${fmtUsd(100 * f.lev)} position. Higher leverage needs less margin but the same price move is a bigger share of it.`,
    },
  };
  for (const [k, learn] of Object.entries(LEARN) as [OrderHelpKey, string][]) help[k] = { ...help[k], learn };
  return help;
}
